import { Injectable } from '@angular/core';
import { CovanceGridColumn, dateTimeComparator, dateRenderer } from '../../covance-ag-grid';
import { DetailModalPopupAgRendererComponent } from '../../covance-ag-grid/renderers/detail-modal-popup-renderer.component';
import { KitInfoGridRowItem } from './kit-grid-row';
import { FilterFieldValue, FilterFieldType } from '../../covance-filter';
import { ICellRendererParams } from 'ag-grid-community';
import { KitDetail } from './kit-detail';
import { KitExpiration } from '../../sponsor/kit-inventory/kit-expiration-report';
import * as moment from 'moment';

export interface KitDetailGridRowItem extends ICellRendererParams {
    data: KitDetail;
}

export interface KitExpirationGridRowItem extends ICellRendererParams {
    data: KitExpiration;
}

@Injectable()
export class KitInventoryColumns {

    public kitType: CovanceGridColumn = {
        headerName: 'Kit Type',
        field: 'kitType',
        colId: 'kitType',
        sort: 'asc',
        width: 50
    };

    public description: CovanceGridColumn = {
        headerName: 'Description',
        field: 'description',
        colId: 'description',
        cellRendererFramework: DetailModalPopupAgRendererComponent,
        width: 220
    };

    public descriptionExpiration: CovanceGridColumn = {
        headerName: 'Description',
        field: 'description',
        colId: 'description',
        width: 220
    };

    public status: CovanceGridColumn = {
        headerName: 'Status',
        field: 'status',
        colId: 'status',
        width: 70,
        valueGetter: (params: KitInfoGridRowItem) => {
            if (!params.data) {
                return null;
            } else if (params.data.status === '0') {
                return 'Active';
            } else if (params.data.status === '1') {
                return 'Inactive';
            } else if (params.data.status === '2') {
                return 'On Hold';
            } else {
                return null;
            }
        }
    };

    public pending: CovanceGridColumn = {
        headerName: 'Pending Shipment',
        field: 'pending',
        colId: 'pending',
        width: 70
    };

    public shipped: CovanceGridColumn = {
        headerName: 'Shipped to Site',
        field: 'shipped',
        colId: 'shipped',
        width: 70
    };

    public received: CovanceGridColumn = {
        headerName: 'Received by Lab',
        field: 'received',
        colId: 'received',
        width: 70
    };

    public adjusted: CovanceGridColumn = {
        headerName: 'Adjusted by Site',
        field: 'adjusted',
        colId: 'adjusted',
        width: 70
    };

    public onHand: CovanceGridColumn = {
        headerName: 'On Hand',
        field: 'onHand',
        colId: 'onHand',
        width: 90
    };

    public lastShipmentQuantity: CovanceGridColumn = {
        headerName: 'Last Shipment Quantity',
        field: 'lastshipmentQuantity',
        colId: 'lastshipmentQuantity',
        width: 70
    };

    public lastShipmentDate: CovanceGridColumn = {
        headerName: 'Last Shipment Date',
        field: 'lastShipmentDate',
        colId: 'lastShipmentDate',
        comparator: dateTimeComparator,
        valueGetter: (params: KitInfoGridRowItem) => {
            if (!params.data) {
                return null;
            }
            if (params.data.lastShipmentDate.isBefore([1000, 1, 1])) {
                return 'N/A';
            }
            return dateRenderer({ value: params.data.lastShipmentDate });
        }
    };

    public investigatorFullName: CovanceGridColumn = {
        headerName: 'Site',
        field: 'investigatorFullName',
        colId: 'investigatorFullName',
        width: 220,
        sort: 'asc',
        valueGetter: (params: KitExpirationGridRowItem) => {
            return params.data.siteNumber + ' - ' + params.data.investigatorFullName;
        }
    };

    public country: CovanceGridColumn = {
        headerName: 'Country',
        field: 'country',
        colId: 'country',
        width: 70,
        sort: 'asc'
    };

    public region: CovanceGridColumn = {
        headerName: 'Region',
        field: 'region',
        colId: 'region',
        width: 70,
        sort: 'asc'
    };

    public projectNumber: CovanceGridColumn = {
        headerName: 'Project No.',
        field: 'projectNumber',
        colId: 'projectNumber',
        width: 90,
        sort: 'asc'
    };

    public accessionNumber: CovanceGridColumn = {
        headerName: 'Accession',
        field: 'accessionNumber',
        colId: 'accessionNumber',
        width: 120
    };

    public shippedDate: CovanceGridColumn = {
        headerName: 'Shipped Date',
        field: 'shippedDate',
        colId: 'shippedDate',
        comparator: dateTimeComparator,
        width: 120,
        valueGetter: (params: KitExpirationGridRowItem) => {
            if (!params.data) {
                return null;
            }
            if (params.data.shippedDate.isBefore([1000, 1, 1])) {
                return 'N/A';
            }
            return dateRenderer({ value: params.data.shippedDate });
        }
    };

    public expirationDate: CovanceGridColumn = {
        headerName: 'Expiration Date',
        field: 'expirationDate',
        colId: 'expirationDate',
        comparator: dateTimeComparator,
        width: 120,
        valueGetter: (params: KitExpirationGridRowItem) => {
            if (!params.data) {
                return null;
            }
            if (params.data.expirationDate.isBefore([1000, 1, 1])) {
                return 'N/A';
            }
            return dateRenderer({ value: params.data.expirationDate });
        }
    };

    public daysToexpiration: CovanceGridColumn = {
        headerName: 'Days to Expiration',
        field: 'daysToExp',
        colId: 'daysToExp',
        valueGetter: (params: KitExpirationGridRowItem) => {
            if (!params.data) {
                return null;
            }
            if (params.data.expirationDate.isBefore([1000, 1, 1])) {
                return 'N/A';
            }
            return params.data.expirationDate.diff(moment(), 'days');
        },
        cellStyle: (params: KitExpirationGridRowItem) => {
            if (!params.data) {
                return null;
            }
            if (params.data.expirationDate.isBefore([1000, 1, 1])) {
                return null;
            }
            var numberDays = params.data.expirationDate.diff(moment(), 'days');
            if ((numberDays > 0) && (numberDays < 30)) {
                return { color: 'orange' };
            }
            if (numberDays <= 0) {
                return { color: 'red' };
            }
            return null;
        },
        tooltip: (params: KitExpirationGridRowItem) => {
            if (!params.data) {
                return null;
            }
            if (params.data.expirationDate.isBefore([1000, 1, 1])) {
                return params.data.accessionNumber + ' has no expiration date.';
            }
            var numberDays = params.data.expirationDate.diff(moment(), 'days');
            if ((numberDays > 0) && (numberDays < 30)) {
                return params.data.accessionNumber + ' is within 30 days of expiration.';
            }
            if (numberDays <= 0) {
                return params.data.accessionNumber + ' is past expiration.';
            }
            return null;
        }
    };

    public investigatorColumns: CovanceGridColumn[] = [
        this.kitType,
        this.description,
        this.status,
        this.pending,
        this.shipped,
        this.received,
        this.adjusted,
        this.onHand,
        this.lastShipmentQuantity,
        this.lastShipmentDate
    ];

    public sponsorColumnsOnHand: CovanceGridColumn[] = [
        this.projectNumber,
        this.region,
        this.country,
        this.investigatorFullName,
        this.kitType,
        this.description,
        this.pending,
        this.shipped,
        this.received,
        this.adjusted,
        this.onHand,
        this.lastShipmentQuantity,
        this.lastShipmentDate
    ];

    public sponsorColumnsExpiration: CovanceGridColumn[] = [
        this.projectNumber,
        this.region,
        this.country,
        this.investigatorFullName,
        this.kitType,
        this.descriptionExpiration,
        this.accessionNumber,
        this.shippedDate,
        this.expirationDate,
        this.daysToexpiration
    ];

    public filterDefinitions: FilterFieldValue[] = [
        {
            def: {
                title: 'Kit Type',
                queryParam: 'kitType',
                isServerSideFilter: true,
                type: FilterFieldType.MultiSelect,
            }
        },
        {
            def: {
                title: 'Description',
                queryParam: 'description',
                isServerSideFilter: true,
                type: FilterFieldType.MultiSelect,
            }
        },
        {
            def: {
                title: 'Status',
                queryParam: 'status',
                isServerSideFilter: true,
                type: FilterFieldType.MultiSelect
            }
        }
    ];

    public expirationReportfilterDefinitions: FilterFieldValue[] = [
        {
            def: {
                title: 'Site',
                queryParam: 'site',
                isServerSideFilter: true,
                type: FilterFieldType.MultiSelect
            }
        },
        {
            def: {
                title: 'Expiration Date Range',
                queryParam: 'expirationDate',
                isServerSideFilter: true,
                type: FilterFieldType.DateRange,
            }
        },
        {
            def: {
                title: 'Shipped Date Range',
                queryParam: 'date',
                isServerSideFilter: true,
                type: FilterFieldType.DateRange,
            }
        },
        {
            def: {
                title: 'Kit Type',
                queryParam: 'kitType',
                isServerSideFilter: true,
                type: FilterFieldType.MultiSelect,
            }
        },
        {
            def: {
                title: 'Ignore Record Limit',
                queryParam: 'recordLimit',
                isServerSideFilter: true,
                type: FilterFieldType.Checkbox,
            }
        }
    ];

    public onHandReportfilterDefinitions: FilterFieldValue[] = [
        {
            def: {
                title: 'Site',
                queryParam: 'site',
                isServerSideFilter: true,
                type: FilterFieldType.MultiSelect
            }
        },
        {
            def: {
                title: 'Last Shipped Date Range',
                queryParam: 'date',
                isServerSideFilter: true,
                type: FilterFieldType.DateRange,
            }
        },
        {
            def: {
                title: 'Kit Type',
                queryParam: 'kitType',
                isServerSideFilter: true,
                type: FilterFieldType.MultiSelect,
            }
        }
    ];
}